'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import PageHero from '@/components/layout/PageHero';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, MessageCircle, Clock, Send, CheckCircle } from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const { fullName, email, subject, message } = formData;
    
    if (!fullName.trim()) return 'Please enter your full name';
    if (!email.trim()) return 'Please enter your email address';
    if (!email.includes('@')) return 'Please enter a valid email address';
    if (!subject) return 'Please select a subject';
    if (!message.trim()) return 'Please enter your message';
    if (message.trim().length < 10) return 'Message must be at least 10 characters long';
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    setFormData({
      fullName: '',
      email: '',
      subject: '',
      message: ''
    });

    // Hide success message after 5 seconds
    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
      <PageHero
        title="We'd Love to Hear from You!"
        subtitle="Send us your questions, ideas, or feedback — we're here to help."
        variant="strip"
        background="slate-blur"
        titleGradient="from-blue-300 to-indigo-400"
      />

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50">
                <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-slate-200 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        placeholder="Enter your email address"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-slate-200 mb-2">
                      Subject *
                    </label>
                    <Select value={formData.subject} onValueChange={(value) => handleInputChange('subject', value)}>
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="bug-report" className="text-white hover:bg-slate-600">Bug Report</SelectItem>
                        <SelectItem value="feature-request" className="text-white hover:bg-slate-600">Feature Request</SelectItem>
                        <SelectItem value="help-needed" className="text-white hover:bg-slate-600">Help Needed</SelectItem>
                        <SelectItem value="feedback" className="text-white hover:bg-slate-600">Feedback</SelectItem>
                        <SelectItem value="business-inquiry" className="text-white hover:bg-slate-600">Business Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-200 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent min-h-[120px]"
                      placeholder="Tell us how we can help you..."
                      spellCheck={true}
                      required
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Minimum 10 characters required
                    </p>
                  </div>

                  <div className="text-center pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>

                {/* Success Message */}
                {showSuccess && (
                  <div className="mt-6 bg-green-600/20 border border-green-500/50 rounded-xl p-4 flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div>
                      <h3 className="text-green-400 font-medium">Message Sent!</h3>
                      <p className="text-green-300 text-sm">Thanks! We&apos;ll get back to you shortly.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Panel */}
            <div className="lg:col-span-1">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 h-fit">
                <h3 className="text-xl font-bold text-white mb-6">Quick Info</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Email Support</h4>
                      <p className="text-slate-300 text-sm">support@indiestry.com</p>
                      <p className="text-slate-400 text-xs mt-1">We typically respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Discord Community</h4>
                      <p className="text-slate-300 text-sm">@IndiestrySupport</p>
                      <p className="text-slate-400 text-xs mt-1">Join our community for real-time help</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Support Hours</h4>
                      <p className="text-slate-300 text-sm">Mon–Fri, 9am–5pm PST</p>
                      <p className="text-slate-400 text-xs mt-1">We&apos;re here to help during business hours</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-700/50">
                  <h4 className="text-white font-medium mb-3">Common Questions</h4>
                  <div className="space-y-2">
                    <Link href="/faq" className="block text-slate-300 hover:text-blue-400 text-sm transition-colors">
                      How do I get started with voice acting?
                    </Link>
                    <Link href="/faq" className="block text-slate-300 hover:text-blue-400 text-sm transition-colors">
                      What equipment do I need?
                    </Link>
                    <Link href="/faq" className="block text-slate-300 hover:text-blue-400 text-sm transition-colors">
                      How do I find voice acting jobs?
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
} 